/**
 * Firebase client-side configuration.
 *
 * This file initializes the Firebase app for client-side auth (Google sign-in).
 * The Firebase project ID comes from environment variables.
 *
 * NOTE: These are PUBLIC client-side config values — they are NOT secrets.
 * Firebase client config is safe to embed in frontend code.
 */
import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  initializeAuth,
  browserPopupRedirectResolver,
  inMemoryPersistence,
  GoogleAuthProvider,
  getRedirectResult,
  signInWithRedirect,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  signOut as firebaseSignOut,
  updateProfile,
  type Auth,
} from "firebase/auth";

// Firebase config — uses environment variables set per app
// In Vite: VITE_FIREBASE_PROJECT_ID, VITE_FIREBASE_API_KEY, etc.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
  projectId:
    import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "badminton-warriors-dev",
};

let app: FirebaseApp;
let auth: Auth;
const GOOGLE_REDIRECT_PENDING_KEY = "bw.googleRedirect.pending.v1";
const firebaseAuthOptions = {
  // Keep Firebase credentials ephemeral in the browser.
  persistence: inMemoryPersistence,
  popupRedirectResolver: browserPopupRedirectResolver,
};

function getFirebaseAuth(): Auth {
  if (!auth) {
    const existingApp = getApps()[0];
    app = existingApp ?? initializeApp(firebaseConfig);
    try {
      auth = initializeAuth(app, firebaseAuthOptions);
    } catch (error) {
      if (!existingApp) {
        throw error;
      }
      // If another entry point already initialized auth for this app, reuse it.
      auth = getAuth(app);
    }
  }
  return auth;
}

const googleProvider = new GoogleAuthProvider();
const GOOGLE_SIGN_IN_DISABLED_CODE = "auth/google-sign-in-disabled";

function googleRedirectSignInEnabled(): boolean {
  return import.meta.env.VITE_GOOGLE_SIGN_IN_ENABLED !== "false";
}

function redirectSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    // Redirect auth still has Firebase's own state when browser privacy
    // settings reject sessionStorage. Callers simply cannot distinguish an
    // unrelated bootstrap failure from a redirect-return failure in that case.
    return null;
  }
}

function setPendingGoogleRedirect(value: boolean): void {
  const storage = redirectSessionStorage();
  if (!storage) return;
  try {
    if (value) {
      storage.setItem(GOOGLE_REDIRECT_PENDING_KEY, "1");
    } else {
      storage.removeItem(GOOGLE_REDIRECT_PENDING_KEY);
    }
  } catch {
    // Storage is an advisory UX/safety marker, never an auth credential.
  }
}

/** Whether this browser tab explicitly started a Google redirect sign-in. */
export function hasPendingGoogleRedirectSignIn(): boolean {
  const storage = redirectSessionStorage();
  if (!storage) return false;
  try {
    return storage.getItem(GOOGLE_REDIRECT_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Start Google authentication in the current tab.
 *
 * The app's configured auth domain serves Firebase's helper under the same
 * site, so mobile browsers do not need to create or retain a popup window.
 * The ID token is deliberately consumed only after the browser returns and
 * the app mounts again.
 */
export async function beginGoogleRedirectSignIn(): Promise<void> {
  if (!googleRedirectSignInEnabled()) {
    throw Object.assign(
      new Error("Google redirect sign-in is disabled for this deployment."),
      { code: GOOGLE_SIGN_IN_DISABLED_CODE },
    );
  }
  const firebaseAuth = getFirebaseAuth();
  setPendingGoogleRedirect(true);
  try {
    await signInWithRedirect(firebaseAuth, googleProvider);
  } catch (error) {
    // If navigation never began, a later ordinary page load must not be
    // mistaken for a failed account switch.
    setPendingGoogleRedirect(false);
    throw error;
  }
}

/**
 * Consume exactly one returned Google redirect result, if this page load was
 * initiated by Google sign-in. The BFF remains the owner of the web session;
 * callers receive only a fresh Firebase token for that single exchange.
 */
export async function consumeGoogleRedirectSignIn(): Promise<string | null> {
  const firebaseAuth = getFirebaseAuth();
  try {
    const result = await getRedirectResult(firebaseAuth);
    if (!result) return null;
    return await result.user.getIdToken(true);
  } finally {
    // Consume at most one returned operation. A stale marker must never make
    // future ordinary page loads look like a failed redirect.
    setPendingGoogleRedirect(false);
  }
}

export type EmailVerificationStatus = "sent" | "already_verified";
export type PendingEmailVerificationStatus =
  | EmailVerificationStatus
  | "delivery_failed";

export type PendingSignupResult =
  | PendingEmailVerificationStatus
  | {
      deliveryStatus: PendingEmailVerificationStatus;
      accountNameIncomplete: true;
    };

export interface EmailSignupResult {
  idToken: string;
  deliveryStatus: PendingEmailVerificationStatus;
  accountNameIncomplete?: true;
}

/**
 * Sign up with email and password and request verification delivery through
 * the authenticated BFF bridge.
 * Returns the Firebase ID token together with the verification-delivery
 * outcome. Account creation remains recoverable when only delivery fails.
 */
export async function signUpWithEmail(
  email: string,
  password: string,
  displayName?: string,
  returnUrl?: string,
): Promise<EmailSignupResult> {
  const firebaseAuth = getFirebaseAuth();
  const result = await createUserWithEmailAndPassword(
    firebaseAuth,
    email,
    password,
  );
  let accountNameIncomplete: true | undefined;
  if (displayName && displayName.trim()) {
    try {
      await updateProfile(result.user, { displayName: displayName.trim() });
    } catch {
      // Optional profile completion must not strand an already-created account
      // before verification. The caller shows a recoverable warning afterward.
      accountNameIncomplete = true;
    }
  }
  const idToken = await result.user.getIdToken(true);
  try {
    const deliveryStatus = await requestEmailVerification(idToken, returnUrl);
    return {
      idToken,
      deliveryStatus,
      ...(accountNameIncomplete ? { accountNameIncomplete } : {}),
    };
  } catch {
    // Account creation and token minting already succeeded. Every subsequent
    // delivery-bridge failure (including 429) must remain recoverable; throwing
    // here would strand the new account and make retrying registration fail as
    // already-in-use.
    return {
      idToken,
      deliveryStatus: "delivery_failed",
      ...(accountNameIncomplete ? { accountNameIncomplete } : {}),
    };
  }
}

/**
 * Sign in with email and password.
 * Returns the Firebase ID token.
 */
export async function signInWithEmail(
  email: string,
  password: string,
): Promise<string> {
  const firebaseAuth = getFirebaseAuth();
  const result = await signInWithEmailAndPassword(
    firebaseAuth,
    email,
    password,
  );
  const idToken = await result.user.getIdToken(true);
  return idToken;
}

/** Reconfirm the existing personal account; never link or create an identity. */
export async function updateAccountName(
  expectedUserId: string,
  email: string,
  password: string,
  displayName: string,
  beforeUpdate: () => Promise<void>,
  assertCurrent: () => void,
): Promise<void> {
  const name = displayName.trim();
  if (
    !expectedUserId ||
    expectedUserId.startsWith("work:") ||
    !email ||
    !name
  ) {
    throw Object.assign(new Error("Personal account name required"), {
      code: "auth/account-name-invalid",
    });
  }
  const credential = await signInWithEmailAndPassword(
    getFirebaseAuth(),
    email,
    password,
  );
  if (credential.user.uid !== expectedUserId) {
    throw Object.assign(new Error("Account changed"), {
      code: "auth/account-changed",
    });
  }
  await beforeUpdate();
  assertCurrent();
  try {
    // This is deliberately the only provider field written by this operation.
    await updateProfile(credential.user, { displayName: name });
  } catch {
    // A lost response is not proof that Firebase did not apply the update.
    throw Object.assign(new Error("Account name outcome unconfirmed"), {
      code: "auth/account-name-unconfirmed",
    });
  }
}

/**
 * Send password reset email.
 */
export async function resetPassword(email: string): Promise<void> {
  const firebaseAuth = getFirebaseAuth();
  await sendPasswordResetEmail(firebaseAuth, email);
}

/**
 * Re-send the email-verification link to the CURRENT Firebase user (#1307).
 *
 * Used by the sign-up "verify your email" screen's Resend button. `signUpWithEmail`
 * already sends the first verification email and leaves the new account as the
 * Firebase current-user (no BFF session is established on the sign-up path), so
 * this targets that same user. Throws if there is no signed-in user.
 */
export async function resendVerificationEmail(
  returnUrl?: string,
): Promise<EmailVerificationStatus> {
  const firebaseAuth = getFirebaseAuth();
  const user = firebaseAuth.currentUser;
  if (!user) {
    throw verificationError(
      "auth/no-current-user",
      "Sign in to request a verification email.",
    );
  }
  const idToken = await user.getIdToken(true);
  return await requestEmailVerification(idToken, returnUrl);
}

type VerificationError = Error & { code: string };

function verificationError(code: string, message: string): VerificationError {
  return Object.assign(new Error(message), { code });
}

async function requestEmailVerification(
  idToken: string,
  returnUrl?: string,
): Promise<EmailVerificationStatus> {
  let response: Response;
  try {
    response = await fetch("/bff/auth/email-verification", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken, returnUrl }),
    });
  } catch {
    throw verificationError(
      "email-verification/unavailable",
      "Verification email request failed.",
    );
  }

  const body = (await response.json().catch(() => ({}))) as {
    code?: unknown;
    status?: unknown;
  };
  if (
    response.ok &&
    (body.status === "sent" || body.status === "already_verified")
  ) {
    return body.status;
  }

  if (
    response.status === 429 ||
    body.code === "EMAIL_VERIFICATION_RATE_LIMITED"
  ) {
    throw verificationError(
      "auth/too-many-requests",
      "Too many verification email requests.",
    );
  }
  throw verificationError(
    "email-verification/unavailable",
    "Verification email is temporarily unavailable.",
  );
}

/**
 * Verify a password-reset oobCode (from the email-action URL) WITHOUT
 * consuming it. Resolves with the email of the account the code belongs to;
 * rejects with auth/invalid-action-code or auth/expired-action-code for a
 * dead link. Used by /reset-password landing pages to pre-validate the link
 * BEFORE asking the user to type a new password.
 */
export async function verifyResetCode(oobCode: string): Promise<string> {
  const firebaseAuth = getFirebaseAuth();
  return await verifyPasswordResetCode(firebaseAuth, oobCode);
}

/**
 * Confirm a password reset using oobCode from the email-action URL.
 * Used by /reset-password landing pages.
 */
export async function confirmReset(
  oobCode: string,
  newPassword: string,
): Promise<void> {
  const firebaseAuth = getFirebaseAuth();
  await confirmPasswordReset(firebaseAuth, oobCode, newPassword);
}

/**
 * Sign out from Firebase.
 */
export async function signOutFirebase(): Promise<void> {
  const firebaseAuth = getFirebaseAuth();
  await firebaseSignOut(firebaseAuth);
}
